import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Mail, Calendar, Calculator, Ruler } from 'lucide-react';
import { fetchAdminCalculations } from '../../services/adminApi';

const PageContainer = styled.div`
  width: 100%;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, var(--primary-light) 0%, rgba(255, 143, 0, 0.05) 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 143, 0, 0.2);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: var(--gray-100);
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid var(--gray-200);

  &:hover {
    background-color: var(--gray-50);
  }
`;

const Td = styled.td`
  padding: 1rem;
  font-size: 0.9375rem;
  color: var(--gray-700);
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--gray-500);
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.125rem;
  color: var(--gray-500);
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.125rem;
  color: var(--danger-color);
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: var(--primary-light);
  color: var(--primary-color);
  white-space: nowrap;
`;

const DimensionsText = styled.span`
  font-family: 'Courier New', monospace;
  color: var(--secondary-color);
`;

const PoleCalculationsPage = () => {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCalculations();
  }, []);

  const fetchCalculations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAdminCalculations();
      setCalculations(data || []);
    } catch (err) {
      console.error('Error fetching pole calculations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRecentCalculations = () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return calculations.filter(calc =>
      new Date(calc.created_at) > sevenDaysAgo
    ).length;
  };

  const getUniquePoleTypes = () => {
    return new Set(calculations.map(calc => calc.pole_type)).size;
  };

  if (loading) {
    return <LoadingText>Loading pole calculations...</LoadingText>;
  }

  if (error) {
    return <ErrorText>Error loading calculations: {error}</ErrorText>;
  }

  return (
    <PageContainer>
      <StatsContainer>
        <StatCard>
          <StatValue>{calculations.length}</StatValue>
          <StatLabel>Total Calculations</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{getRecentCalculations()}</StatValue>
          <StatLabel>Last 7 Days</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{getUniquePoleTypes()}</StatValue>
          <StatLabel>Pole Types Used</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>
            {calculations.length > 0
              ? Math.round(calculations.reduce((sum, c) => sum + (parseFloat(c.bag_height) || 0), 0) / calculations.length)
              : 0}cm
          </StatValue>
          <StatLabel>Avg Bag Height</StatLabel>
        </StatCard>
      </StatsContainer>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>User Email</Th>
              <Th>Pole Type</Th>
              <Th>Bag Dimensions</Th>
              <Th>Loop Details</Th>
              <Th>Calculated Length</Th>
              <Th>Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {calculations.map((calc) => (
              <Tr key={calc.id}>
                <Td>
                  <IconWrapper>
                    <Mail size={16} />
                    {calc.user_email}
                  </IconWrapper>
                </Td>
                <Td>
                  <Badge>{calc.pole_type}</Badge>
                </Td>
                <Td>
                  <IconWrapper>
                    <Ruler size={16} />
                    <DimensionsText>
                      {calc.bag_length} × {calc.bag_width} × {calc.bag_height} cm
                    </DimensionsText>
                  </IconWrapper>
                </Td>
                <Td>
                  <div style={{ fontSize: '0.875rem' }}>
                    <div>{calc.loop_type}</div>
                    <div style={{ color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                      {calc.loop_length} cm
                    </div>
                  </div>
                </Td>
                <Td>
                  <IconWrapper>
                    <Calculator size={16} />
                    <strong style={{ color: 'var(--primary-color)' }}>
                      {calc.calculated_pole_length}
                    </strong>
                  </IconWrapper>
                </Td>
                <Td>
                  <IconWrapper>
                    <Calendar size={16} />
                    {formatDate(calc.created_at)}
                  </IconWrapper>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default PoleCalculationsPage;
